import classnames from "classnames";
import { useHistory, useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import type { Room } from "@/react/types/Live/Room";
import { formatDateString } from "@circle-react/helpers/dateTimeHelpers/getLocalizedDate";
import { mobileCellStyles } from "@circle-react-shared/uikit/TableV2/constants";
import { DataTable } from "@circle-react-uikit/DataTable";
import { Actions } from "./Actions";
import { DetailsModal } from "./DetailsModal";
import { LiveStreamStatus } from "./LiveStreamStatus";
import "./styles.scss";

export interface LiveStreamsTableProps {
  className?: string;
  currentRoom: Room | null;
  isLoadingRoom: boolean;
  onCloseModal: () => void;
  records: any[];
  refetch: () => void;
}

export const LiveStreamsTable = ({
  records,
  refetch,
  className,
  currentRoom,
  isLoadingRoom,
  onCloseModal,
}: LiveStreamsTableProps) => {
  const history = useHistory();
  const location = useLocation();

  const columns = [
    {
      title: t("live_streams.dashboard.table.name"),
      dataIndex: "name",
      key: "name",
      variant: "uppercase",
    },
    {
      title: t("live_streams.dashboard.table.status"),
      dataIndex: "status",
      key: "status",
      variant: "uppercase",
      className: mobileCellStyles.sm,
      tdClassName: mobileCellStyles.sm,
    },
    {
      title: t("live_streams.dashboard.table.date"),
      dataIndex: "live_at",
      key: "live_at",
      variant: "uppercase",
      className: mobileCellStyles.xl,
      tdClassName: mobileCellStyles.xl,
    },
    {
      title: t("live_streams.dashboard.table.recording"),
      dataIndex: "recording",
      key: "recording",
      variant: "uppercase",
    },
    {
      title: t("live_streams.dashboard.table.participants"),
      dataIndex: "total_participants",
      key: "total_participants",
      variant: "uppercase",
      className: mobileCellStyles.md,
      tdClassName: mobileCellStyles.md,
    },
    {
      title: t("live_streams.dashboard.table.type"),
      dataIndex: "type",
      key: "type",
      variant: "uppercase",
      className: mobileCellStyles.xl,
      tdClassName: mobileCellStyles.xl,
    },
    {
      title: " ",
      dataIndex: "actions",
      key: "actions",
      variant: "actions",
    },
  ] as const;

  const handleOpenModal = (room: Room) => {
    const urlParams = new URLSearchParams(location.search);
    const search = urlParams.toString();
    history.push({
      pathname: `/settings/live_streams/${room.id}`,
      search: search ? `?${search}` : "",
    });
  };

  const transformedRecords =
    records?.map(record => ({
      ...record,
      key: record.id,
      name: (
        <button
          type="button"
          className="max-w-[240px] cursor-pointer truncate"
          title={record.name}
          onClick={() => handleOpenModal(record)}
        >
          {record.name}
        </button>
      ),
      status: <LiveStreamStatus status={record.status} />,
      live_at: formatDateString(record.live_at, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      recording: (
        <a
          className={classnames(
            "text-dark inline max-w-sm truncate hover:text-black",
            {
              "cursor-text": !record.recording_url,
            },
          )}
          title={t("live_streams.dashboard.table.actions.download")}
          href={record.recording_url}
          download
          target="_blank"
          rel="noreferrer"
        >
          {record.recording_url
            ? t("live_streams.dashboard.table.actions.download")
            : "-"}
        </a>
      ),
      type:
        record.room_type == "conference"
          ? t("live_streams.dashboard.table.room")
          : t("live_streams.dashboard.table.stream"),
      actions: (
        <Actions
          room={record}
          refetch={refetch}
          onOpenModal={handleOpenModal}
        />
      ),
      rowClassName: "group",
    })) || [];

  return (
    <>
      <DetailsModal
        isOpen={!!currentRoom}
        onClose={onCloseModal}
        room={currentRoom}
        isLoading={isLoadingRoom}
        refetch={refetch}
      />
      <DataTable
        columns={columns}
        dataSource={transformedRecords}
        className={className}
      />
    </>
  );
};
